import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Search,
  FileText,
  Download,
  Lock,
  ShieldAlert,
  ChevronDown,
  ChevronRight,
  X,
} from 'lucide-react';
import toast from 'react-hot-toast';

import PageHeader from '../../components/layout/PageHeader';
import EmptyState from '../../components/ui/EmptyState';
import Button from '../../components/ui/Button';
import Spinner from '../../components/ui/Spinner';
import DocumentPreviewModal, {
  type PreviewContentKind,
} from '../../components/ui/DocumentPreviewModal';
import SearchableSelect from '../../components/ui/SearchableSelect';
import HierarchyNodePicker from '../../components/hierarchy/HierarchyNodePicker';
import hierarchyService from '../../services/hierarchy.service';
import Table, { type TableColumn, type TablePagination } from '../../components/ui/Table';
import { searchService } from '../../services/search.service';
import documentService from '../../services/document.service';
import { ROUTES } from '../../config/routes.config';
import { useAuthStore } from '../../store/authStore';
import { formatDocumentDisplayName } from '../../utils/documentName';
import {
  canDownloadDocument,
  canPreviewDocument,
} from '../../utils/documentAccessMode';
import { getFilterYearSelectOptions } from '../../utils/filterYearOptions';
import type {
  CaseSearchResult,
  HashtagSearchResult,
  CaseSearchParams,
  PaginationMeta,
} from '../../types/search.types';

const SEARCH_DOC_LIMIT = 10;

function resultCount(rows: HashtagSearchResult[], pagination: PaginationMeta | null): number {
  return pagination?.total ?? rows.length;
}

function toTablePagination(
  meta: PaginationMeta | null,
  fallbackPage: number
): TablePagination | undefined {
  if (!meta || meta.totalPages <= 1) return undefined;
  return {
    page: meta.page ?? fallbackPage,
    limit: meta.limit ?? SEARCH_DOC_LIMIT,
    total: meta.total,
    totalPages: meta.totalPages,
    hasNextPage: meta.hasNextPage,
    hasPrevPage: meta.hasPrevPage,
  };
}

function normalizeSearchTag(raw: string): string {
  return raw.trim().replace(/^#+/, '').toLowerCase().replace(/[\s_]+/g, '-');
}

const FIND_HIGHLIGHT_STYLE: React.CSSProperties = {
  backgroundColor: '#ffe566',
  color: 'inherit',
  padding: 0,
  borderRadius: 2,
  boxDecorationBreak: 'clone',
  WebkitBoxDecorationBreak: 'clone',
};

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/** Chrome Ctrl+F style in-cell highlight (case-insensitive). */
function HighlightedFindText({ text, query }: { text: string; query: string }) {
  const raw = String(text ?? '');
  const q = query.trim();
  if (!q) return <>{raw}</>;

  const parts = raw.split(new RegExp(`(${escapeRegExp(q)})`, 'gi'));
  if (parts.length <= 1) return <>{raw}</>;

  return (
    <>
      {parts.map((part, index) =>
        index % 2 === 1 ? (
          <mark key={`${index}-${part}`} style={FIND_HIGHLIGHT_STYLE}>
            {part}
          </mark>
        ) : (
          <React.Fragment key={`${index}-${part}`}>{part}</React.Fragment>
        )
      )}
    </>
  );
}

function withTableFindHighlight<T>(
  columns: TableColumn<T>[],
  query: string
): TableColumn<T>[] {
  const q = query.trim();
  if (!q) return columns;

  return columns.map((col) => ({
    ...col,
    render: (row: T, index: number) => {
      const content = col.render(row, index);
      if (typeof content === 'string' || typeof content === 'number') {
        return <HighlightedFindText text={String(content)} query={q} />;
      }
      return content;
    },
    tooltip: col.tooltip
      ? (row: T, index: number) => {
          const tip = col.tooltip?.(row, index);
          return tip != null ? String(tip) : undefined;
        }
      : undefined,
  }));
}

function buildHashtagRowFindText(
  doc: HashtagSearchResult,
  formatFileSize: (bytes?: number | null) => string
): string {
  const parts = [
    doc.case?.cms_number,
    formatDocumentDisplayName({
      cmsNumber: doc.case?.cms_number,
      originalFileName: doc.original_file_name ?? doc.file_name ?? '',
      documentType: doc.document_type,
    }),
    doc.folder_type,
    doc.document_type,
    doc.page_count != null ? String(doc.page_count) : '',
    formatFileSize(doc.file_size_bytes ?? null),
    doc.classification,
    doc.approval_status,
    doc.case?.ps_name,
    doc.case?.rrms_ref_number,
  ];
  return parts
    .map((p) => String(p ?? '').trim())
    .filter((p) => p && p !== '—')
    .join(' ')
    .toLowerCase();
}

function filterRowsByTableFind<T>(
  rows: T[],
  query: string,
  getText: (row: T) => string
): T[] {
  const q = query.trim().toLowerCase();
  if (!q) return rows;
  return rows.filter((row) => getText(row).includes(q));
}

function AccordionTableFindPanel({
  query,
  onQueryChange,
  filteredCount,
  totalOnPage,
  children,
}: {
  query: string;
  onQueryChange: (value: string) => void;
  filteredCount: number;
  totalOnPage: number;
  children: React.ReactNode;
}) {
  const trimmed = query.trim();

  return (
    <div>
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 'var(--spacing-2)',
          marginBottom: 'var(--spacing-3)',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--spacing-2)',
            flex: '1 1 220px',
            minWidth: 200,
            padding: '6px 10px',
            border: '1px solid var(--color-gray-200)',
            borderRadius: 'var(--radius-md)',
            background: 'var(--color-gray-50)',
          }}
        >
          <Search size={16} style={{ color: 'var(--color-gray-500)', flexShrink: 0 }} />
          <input
            type="text"
            value={query}
            onChange={(e) => onQueryChange(e.target.value)}
            placeholder="Search and filter table…"
            aria-label="Search and filter table"
            style={{
              flex: 1,
              border: 'none',
              outline: 'none',
              background: 'transparent',
              fontSize: 'var(--font-size-sm)',
              fontFamily: 'inherit',
            }}
          />
          {trimmed ? (
            <button
              type="button"
              onClick={() => onQueryChange('')}
              aria-label="Clear search"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                width: 18,
                height: 18,
                padding: 0,
                border: 'none',
                background: 'transparent',
                cursor: 'pointer',
                color: 'var(--color-gray-500)',
                lineHeight: 1,
              }}
            >
              <X size={14} strokeWidth={2} aria-hidden />
            </button>
          ) : null}
        </div>
        {trimmed ? (
          <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-gray-500)' }}>
            {filteredCount === 0
              ? `No rows match (${totalOnPage} on this page)`
              : `Showing ${filteredCount} of ${totalOnPage} row${totalOnPage !== 1 ? 's' : ''} on this page`}
          </span>
        ) : null}
      </div>
      {children}
    </div>
  );
}

/** Include typed text in the input as a tag before searching. */
function tagsWithPendingInput(
  tags: string[],
  pendingInput: string
): { tags: string[]; clearedInput: boolean } {
  const pending = normalizeSearchTag(pendingInput);
  if (!pending) return { tags, clearedInput: false };
  if (tags.includes(pending)) return { tags, clearedInput: true };
  return { tags: [...tags, pending], clearedInput: true };
}

function SearchLoadingPanel({ message = 'Searching...' }: { message?: string }) {
  return (
    <div
      role="status"
      aria-live="polite"
      aria-busy="true"
      style={{
        marginTop: 'var(--spacing-4)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 'var(--spacing-3)',
        padding: 'var(--spacing-8) var(--spacing-4)',
        border: '1px solid var(--color-gray-200)',
        borderRadius: 'var(--radius-md)',
        background: 'white',
      }}
    >
      <Spinner size={28} />
      <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-gray-600)' }}>
        {message}
      </span>
    </div>
  );
}

type SearchAccordionId = 'hashtag_manual' | 'hashtag_content' | 'ocr_content';

function SearchResultAccordion({
  label,
  count,
  children,
  expanded,
  onExpandedChange,
  isLoading = false,
}: {
  label: string;
  count: number;
  children: React.ReactNode;
  expanded: boolean;
  onExpandedChange: (expanded: boolean) => void;
  isLoading?: boolean;
}) {
  return (
    <div
      style={{
        marginTop: 'var(--spacing-4)',
        border: '1px solid var(--color-gray-200)',
        borderRadius: 'var(--radius-md)',
        background: 'white',
        overflow: 'hidden',
      }}
    >
      <button
        type="button"
        onClick={() => onExpandedChange(!expanded)}
        aria-expanded={expanded}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--spacing-2)',
          padding: 'var(--spacing-3) var(--spacing-4)',
          border: 'none',
          background: 'var(--color-gray-50)',
          cursor: 'pointer',
          textAlign: 'left',
          fontSize: 'var(--font-size-sm)',
          fontWeight: 'var(--font-weight-semibold)',
          color: 'var(--color-gray-800)',
        }}
      >
        {expanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
        <span>
          {count} document{count !== 1 ? 's' : ''} found on {label}
        </span>
      </button>
      {expanded && (
        <div style={{ padding: 'var(--spacing-3) var(--spacing-4)', position: 'relative' }}>
          {isLoading && (
            <div
              style={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'rgba(255,255,255,0.7)',
                zIndex: 1,
              }}
            >
              <Spinner size={24} />
            </div>
          )}
          <div style={{ opacity: isLoading ? 0.6 : 1 }}>{children}</div>
        </div>
      )}
    </div>
  );
}

const LIMIT = 10;
const FILE_EXTENSIONS = ['', 'PDF', 'DOCX', 'XLSX', 'JPG', 'PNG', 'MP4', 'MP3'];
const UPLOAD_STATUS_OPTS: { id: string; name: string }[] = [
  { id: '', name: 'All upload statuses' },
  { id: 'Pending', name: 'Pending' },
  { id: 'Approved', name: 'Approved' },
  { id: 'Rejected', name: 'Denied' },
];
const CLASSIFICATION_OPTS: { id: string; name: string }[] = [
  { id: 'Public', name: 'Public' },
  { id: 'Confidential', name: 'Confidential' },
];
const DEFAULT_FILTERS: CaseSearchParams = {};
type SearchRestoreState = {
  searchMode?: 'filter' | 'hashtag' | 'ocr_hashtag';
  filters?: CaseSearchParams;
  appliedFilters?: CaseSearchParams;
  page?: number;
  hashtagTags?: string[];
  ocrHashtagTags?: string[];
};

export default function SearchDocumentsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { can } = useAuthStore();
  // Reviewers (records.approve / legacy CM+admin) never need access requests.
  const isReviewer = can('records.approve', ['RRMS_CONTENT_MANAGER', 'RRMS_SUPER_ADMIN']);
  // Draft filters edited in the modal
  const [filters, setFilters] = useState<CaseSearchParams>(DEFAULT_FILTERS);
  // Filters that are actually applied to the query
  const [appliedFilters, setAppliedFilters] =
    useState<CaseSearchParams>(DEFAULT_FILTERS);
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  // Bumped on Clear to force the cascading Section/Sub-Section picker to reset.
  const [filterResetKey, setFilterResetKey] = useState(0);
  const [page, setPage] = useState(1);
  const [hashtagSuggestions, setHashtagSuggestions] = useState<string[]>([]);
  const [previewDocId, setPreviewDocId] = useState<number | string | null>(null);
  const [previewContentKind, setPreviewContentKind] =
    useState<PreviewContentKind | null>(null);

  const [searchMode, setSearchMode] = useState<'filter' | 'hashtag' | 'ocr_hashtag'>('filter');
  const [hashtagInput, setHashtagInput] = useState('');
  const [hashtagTags, setHashtagTags] = useState<string[]>([]);
  const [hashtagManualResults, setHashtagManualResults] = useState<HashtagSearchResult[]>([]);
  const [hashtagManualPagination, setHashtagManualPagination] = useState<PaginationMeta | null>(null);
  const [hashtagContentResults, setHashtagContentResults] = useState<HashtagSearchResult[]>([]);
  const [hashtagContentPagination, setHashtagContentPagination] = useState<PaginationMeta | null>(null);
  const [isHashtagSearching, setIsHashtagSearching] = useState(false);
  const [hashtagSearchAttempted, setHashtagSearchAttempted] = useState(false);
  const [isHashtagOptionsOpen, setIsHashtagOptionsOpen] = useState(false);
  const [includeDocumentContentInHashtagSearch, setIncludeDocumentContentInHashtagSearch] =
    useState(false);
  const [lastHashtagSearchIncludedContent, setLastHashtagSearchIncludedContent] =
    useState(false);
  const [ocrHashtagInput, setOcrHashtagInput] = useState('');
  const [ocrHashtagTags, setOcrHashtagTags] = useState<string[]>([]);
  const [ocrHashtagResults, setOcrHashtagResults] = useState<HashtagSearchResult[]>([]);
  const [ocrHashtagPagination, setOcrHashtagPagination] = useState<PaginationMeta | null>(null);
  const [isOcrHashtagSearching, setIsOcrHashtagSearching] = useState(false);
  const [ocrHashtagSearchAttempted, setOcrHashtagSearchAttempted] = useState(false);
  const [hashtagManualPage, setHashtagManualPage] = useState(1);
  const [hashtagContentPage, setHashtagContentPage] = useState(1);
  const [ocrHashtagPage, setOcrHashtagPage] = useState(1);
  const [openAccordions, setOpenAccordions] = useState<Partial<Record<SearchAccordionId, boolean>>>({});
  const [accordionFindQuery, setAccordionFindQuery] = useState<
    Partial<Record<SearchAccordionId, string>>
  >({});
  const [hashtagManualPageLoading, setHashtagManualPageLoading] = useState(false);
  const [hashtagContentPageLoading, setHashtagContentPageLoading] = useState(false);
  const [ocrHashtagPageLoading, setOcrHashtagPageLoading] = useState(false);

  const setAccordionOpen = useCallback((id: SearchAccordionId, open: boolean) => {
    setOpenAccordions((prev) => ({ ...prev, [id]: open }));
  }, []);

  const isAccordionOpen = useCallback(
    (id: SearchAccordionId) => Boolean(openAccordions[id]),
    [openAccordions]
  );
  const prevHashtagTagsRef = useRef<string[]>([]);
  const prevOcrHashtagTagsRef = useRef<string[]>([]);
  const searchedHashtagTagsRef = useRef<string[]>([]);
  const searchedOcrHashtagTagsRef = useRef<string[]>([]);
  const hashtagSearchRequestRef = useRef(0);
  const ocrHashtagSearchRequestRef = useRef(0);

  useEffect(() => {
    document.title = 'RRMS | Search Documents';
  }, []);

  useEffect(() => {
    const restore = (location.state as { restoreSearchState?: SearchRestoreState } | null)
      ?.restoreSearchState;
    if (!restore) return;
    if (restore.searchMode) setSearchMode(restore.searchMode);
    if (restore.filters) setFilters(restore.filters);
    if (restore.appliedFilters) setAppliedFilters(restore.appliedFilters);
    if (Array.isArray(restore.hashtagTags)) setHashtagTags(restore.hashtagTags);
    if (Array.isArray(restore.ocrHashtagTags)) setOcrHashtagTags(restore.ocrHashtagTags);
    if (Number.isFinite(restore.page) && Number(restore.page) > 0) {
      setPage(Number(restore.page));
    }
    navigate(location.pathname, { replace: true, state: null });
  }, [location.pathname, location.state, navigate]);
  const fetchHashtagManualPage = useCallback(async (page: number, tags: string[]) => {
    const requestId = ++hashtagSearchRequestRef.current;
    setHashtagManualPageLoading(true);
    try {
      const manualRes = await searchService.searchByHashtag(tags, page, SEARCH_DOC_LIMIT);
      if (hashtagSearchRequestRef.current !== requestId) return;
      setHashtagManualResults(manualRes.data ?? []);
      setHashtagManualPagination(manualRes.pagination ?? null);
    } catch {
      if (hashtagSearchRequestRef.current !== requestId) return;
      toast.error('Hashtag search failed');
    } finally {
      if (hashtagSearchRequestRef.current === requestId) {
        setHashtagManualPageLoading(false);
      }
    }
  }, []);

  const fetchHashtagContentPage = useCallback(async (page: number, tags: string[]) => {
    const requestId = ++hashtagSearchRequestRef.current;
    setHashtagContentPageLoading(true);
    try {
      const contentRes = await searchService.searchByOcrHashtag(tags, page, SEARCH_DOC_LIMIT);
      if (hashtagSearchRequestRef.current !== requestId) return;
      setHashtagContentResults(contentRes.data ?? []);
      setHashtagContentPagination(contentRes.pagination ?? null);
    } catch {
      if (hashtagSearchRequestRef.current !== requestId) return;
      toast.error('Document content search failed');
    } finally {
      if (hashtagSearchRequestRef.current === requestId) {
        setHashtagContentPageLoading(false);
      }
    }
  }, []);

  const fetchOcrHashtagPage = useCallback(async (page: number, tags: string[]) => {
    const requestId = ++ocrHashtagSearchRequestRef.current;
    setOcrHashtagPageLoading(true);
    try {
      const docRes = await searchService.searchByOcrHashtag(tags, page, SEARCH_DOC_LIMIT);
      if (ocrHashtagSearchRequestRef.current !== requestId) return;
      setOcrHashtagResults(docRes.data ?? []);
      setOcrHashtagPagination(docRes.pagination ?? null);
    } catch {
      if (ocrHashtagSearchRequestRef.current !== requestId) return;
      toast.error('Document content search failed');
    } finally {
      if (ocrHashtagSearchRequestRef.current === requestId) {
        setOcrHashtagPageLoading(false);
      }
    }
  }, []);

  // Auto-refresh hashtag search when a tag is removed
  useEffect(() => {
    const prev = prevHashtagTagsRef.current;
    prevHashtagTagsRef.current = hashtagTags;
    const tagWasRemoved = prev.length > hashtagTags.length;
    if (!tagWasRemoved) return;
    if (hashtagTags.length === 0) {
      setHashtagManualResults([]);
      setHashtagManualPagination(null);
      setHashtagContentResults([]);
      setHashtagContentPagination(null);
      setHashtagSearchAttempted(false);
      searchedHashtagTagsRef.current = [];
      return;
    }
    if (!hashtagSearchAttempted) return;
    searchedHashtagTagsRef.current = hashtagTags;
    setHashtagManualPage(1);
    setHashtagContentPage(1);
    void fetchHashtagManualPage(1, hashtagTags);
    if (lastHashtagSearchIncludedContent) {
      void fetchHashtagContentPage(1, hashtagTags);
    }
  }, [
    hashtagTags,
    lastHashtagSearchIncludedContent,
    hashtagSearchAttempted,
    fetchHashtagManualPage,
    fetchHashtagContentPage,
  ]);

  useEffect(() => {
    const prev = prevOcrHashtagTagsRef.current;
    prevOcrHashtagTagsRef.current = ocrHashtagTags;
    const tagWasRemoved = prev.length > ocrHashtagTags.length;
    if (!tagWasRemoved) return;
    if (ocrHashtagTags.length === 0) {
      setOcrHashtagResults([]);
      setOcrHashtagPagination(null);
      setOcrHashtagSearchAttempted(false);
      searchedOcrHashtagTagsRef.current = [];
      return;
    }
    if (!ocrHashtagSearchAttempted) return;
    searchedOcrHashtagTagsRef.current = ocrHashtagTags;
    setOcrHashtagPage(1);
    void fetchOcrHashtagPage(1, ocrHashtagTags);
  }, [ocrHashtagTags, ocrHashtagSearchAttempted, fetchOcrHashtagPage]);

  const handleHashtagManualPageChange = useCallback(
    (page: number) => {
      setHashtagManualPage(page);
      setAccordionOpen('hashtag_manual', true);
      void fetchHashtagManualPage(page, searchedHashtagTagsRef.current);
    },
    [fetchHashtagManualPage, setAccordionOpen]
  );

  const handleHashtagContentPageChange = useCallback(
    (page: number) => {
      setHashtagContentPage(page);
      setAccordionOpen('hashtag_content', true);
      void fetchHashtagContentPage(page, searchedHashtagTagsRef.current);
    },
    [fetchHashtagContentPage, setAccordionOpen]
  );

  const handleOcrHashtagPageChange = useCallback(
    (page: number) => {
      setOcrHashtagPage(page);
      void fetchOcrHashtagPage(page, searchedOcrHashtagTagsRef.current);
    },
    [fetchOcrHashtagPage]
  );

  const setFilter = useCallback(
    (key: keyof CaseSearchParams, value: string | number | undefined) => {
      setFilters((prev) => ({ ...prev, [key]: value === '' || value === undefined ? undefined : value }));
    },
    []
  );

  const setApprovalStatusFilter = useCallback((label: string) => {
    const opt = UPLOAD_STATUS_OPTS.find((o) => o.name === label);
    const id = opt?.id ?? '';
    setFilters((prev) => ({
      ...prev,
      approval_status:
        id === 'Pending' || id === 'Approved' || id === 'Rejected'
          ? id
          : undefined,
    }));
  }, []);

  // File Category master (admin-created lookup template) — same source the
  // Upload form uses, so filter options track the admin master automatically.
  const lookupTemplatesQuery = useQuery({
    queryKey: ['search-lookup-templates'],
    queryFn: () => hierarchyService.getLookupTemplates(),
  });
  const lookupTemplates: { id: number; template_key: string; name: string }[] =
    lookupTemplatesQuery.data?.data?.data ?? [];
  const categoryTemplate = lookupTemplates.find(
    (t) => /categor/i.test(t.name) || /categor/i.test(t.template_key)
  );
  const categoryNodesQuery = useQuery({
    queryKey: ['search-category-nodes', categoryTemplate?.template_key],
    queryFn: () => hierarchyService.getTemplateNodes(categoryTemplate!.template_key),
    enabled: !!categoryTemplate,
  });
  const categoryOptions: { id: string; name: string }[] = (
    categoryNodesQuery.data?.data?.data ?? []
  ).map((n: any) => ({ id: String(n.id), name: n.label || `#${n.id}` }));
  const categoryLabel = categoryTemplate?.name ?? 'File Category';

  const folderTypesQuery = useQuery({
    queryKey: ['search-folder-types'],
    queryFn: () => searchService.getFolderTypes(),
  });
  const folderTypes = folderTypesQuery.data ?? [];

  const documentTypesQuery = useQuery({
    queryKey: ['search-document-types', filters.folder_type],
    queryFn: () => searchService.getDocumentTypes(filters.folder_type),
    enabled: true,
  });
  const documentTypes = documentTypesQuery.data ?? [];

  const yearOpts = useMemo(() => getFilterYearSelectOptions(), []);

  const casesQuery = useQuery({
    queryKey: ['search-cases', appliedFilters, page],
    queryFn: () =>
      searchService.searchCases({ ...appliedFilters, page, limit: LIMIT }),
    enabled: searchMode === 'filter',
  });

  // Apply filters from the modal
  const handleSearch = () => {
    setAppliedFilters(filters);
    setPage(1);
    setIsFilterModalOpen(false);
  };

  // Reset to default filters and reload default data
  const handleClearFilters = () => {
    setFilters(DEFAULT_FILTERS);
    setAppliedFilters(DEFAULT_FILTERS);
    setFilterResetKey((k) => k + 1);
    setPage(1);
    setIsFilterModalOpen(false);
  };

  const caseItems = (casesQuery.data?.data ?? []) as CaseSearchResult[];
  const casePagination =
    casesQuery.data?.pagination ?? ({
      page,
      limit: LIMIT,
      total: caseItems.length,
      totalPages: 1,
      hasNextPage: false,
      hasPrevPage: false,
    } as const);

  const formatFileSize = (bytes?: number | null): string => {
    if (!bytes || bytes <= 0) return '—';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
  };

  const hasActiveFilters = Object.values(appliedFilters).some(
    (v) => v !== undefined && v !== ''
  );

  const setAccordionFind = useCallback((id: SearchAccordionId, value: string) => {
    setAccordionFindQuery((prev) => ({ ...prev, [id]: value }));
  }, []);

  const hashtagColumns: TableColumn<HashtagSearchResult>[] = useMemo(() => [
    {
      key: 'file_number',
      header: 'File No',
      minWidth: 130,
      clampLines: 2,
      tooltip: (doc) => (doc.case as any)?.fir_number ?? doc.case?.cms_number ?? '—',
      cellStyle: { fontWeight: 600, color: 'var(--color-primary)' },
      render: (doc) => (doc.case as any)?.fir_number ?? doc.case?.cms_number ?? '—',
    },
    {
      key: 'document_name',
      header: 'Document Name',
      minWidth: 240,
      clampLines: 2,
      tooltip: (doc) =>
        formatDocumentDisplayName({
          cmsNumber: doc.case?.cms_number,
          originalFileName: doc.original_file_name ?? doc.file_name ?? '—',
          documentType: doc.document_type,
        }),
      render: (doc) =>
        formatDocumentDisplayName({
          cmsNumber: doc.case?.cms_number,
          originalFileName: doc.original_file_name ?? doc.file_name ?? '—',
          documentType: doc.document_type,
        }),
    },
    {
      key: 'hierarchy_path',
      header: 'Section',
      minWidth: 180,
      clampLines: 2,
      tooltip: (doc) => (doc.case as any)?.hierarchy_path ?? '—',
      render: (doc) => (doc.case as any)?.hierarchy_path ?? '—',
    },
    {
      key: 'category',
      header: 'Category',
      minWidth: 120,
      clampLines: 2,
      tooltip: (doc) => (doc.case as any)?.category ?? '—',
      render: (doc) => (doc.case as any)?.category ?? '—',
    },
    {
      key: 'subject',
      header: 'Subject',
      minWidth: 180,
      clampLines: 2,
      tooltip: (doc) => (doc.case as any)?.subject ?? '—',
      render: (doc) => (doc.case as any)?.subject ?? '—',
    },
    {
      key: 'pages',
      header: 'Pages',
      minWidth: 90,
      align: 'right',
      tooltip: (doc) => String(doc.page_count ?? '—'),
      render: (doc) => (doc.page_count != null ? doc.page_count : '—'),
    },
    {
      key: 'file_size',
      header: 'File Size',
      minWidth: 110,
      tooltip: (doc) => formatFileSize(doc.file_size_bytes ?? null),
      render: (doc) => formatFileSize(doc.file_size_bytes ?? null),
    },
    {
      key: 'classification',
      header: 'Classification',
      minWidth: 130,
      tooltip: (doc) => doc.classification ?? '—',
      render: (doc) => {
        const isConfidential = doc.classification === 'Confidential';

        return (
          <span style={{
            display: 'inline-block',
            padding: '2px 10px',
            borderRadius: 'var(--radius-full)',
            fontSize: 'var(--font-size-xs)',
            fontWeight: 600,
            background: isConfidential ? 'var(--color-error-light)' : 'var(--color-success-light)',
            color: isConfidential ? 'var(--color-error)' : 'var(--color-success)',
          }}>
            {doc.classification ?? '—'}
          </span>
        );
      },
    },
    {
      key: 'actions',
      header: 'Actions',
      minWidth: 220,
      tooltip: () => 'Document actions',
      render: (doc) => {
        const isConfidential = doc.classification === 'Confidential';
        const canView =
          typeof doc.can_view === 'boolean' ? doc.can_view : !isConfidential;
        const isRestricted = isConfidential && !canView && !isReviewer;
        const previewAllowedByType = canPreviewDocument({
          fileName: doc.original_file_name ?? doc.file_name,
          fileExtension: doc.file_extension,
          mimeType: doc.mime_type,
        });
        const downloadAllowedByType = canDownloadDocument({
          fileName: doc.original_file_name ?? doc.file_name,
          fileExtension: doc.file_extension,
          mimeType: doc.mime_type,
        });
        const hideDownloadWhilePdfOrImagePreview =
          previewDocId != null &&
          (previewContentKind === 'pdf' || previewContentKind === 'image') &&
          String(doc.document_id) === String(previewDocId);

        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)', flexWrap: 'wrap' }}>
            {!isRestricted && previewAllowedByType && (
              <button
                type="button"
                onClick={() => setPreviewDocId(doc.document_id)}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '5px 12px', borderRadius: 'var(--radius-md)', border: '1px solid var(--color-gray-200)', background: 'transparent', color: 'var(--color-gray-600)', fontSize: 'var(--font-size-sm)', fontWeight: 500, cursor: 'pointer' }}
              >
                Preview
              </button>
            )}
            {isRestricted ? (
              <button
                type="button"
                onClick={() => navigate(`${ROUTES.ACCESS_REQUEST}?document_id=${doc.document_id}`)}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '5px 12px', borderRadius: 'var(--radius-md)', border: 'none', background: 'var(--color-warning)', color: 'white', fontSize: 'var(--font-size-sm)', fontWeight: 500, cursor: 'pointer' }}
              >
                Request Access
              </button>
            ) : (
              downloadAllowedByType && !hideDownloadWhilePdfOrImagePreview ? (
                <button
                  type="button"
                  onClick={() =>
                    documentService
                      .downloadDocument(
                        String(doc.document_id),
                        formatDocumentDisplayName({
                          cmsNumber: doc.case?.cms_number,
                          originalFileName: (doc as any).original_file_name ?? (doc as any).file_name,
                          documentType: doc.document_type,
                        })
                      )
                      .catch(() => toast.error('Download failed'))
                  }
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '5px 12px', borderRadius: 'var(--radius-md)', border: '1px solid var(--color-primary)', background: 'var(--color-primary)', color: 'white', fontSize: 'var(--font-size-sm)', fontWeight: 500, cursor: 'pointer' }}
                >
                  <Download size={14} />
                  Download
                </button>
              ) : null
            )}
          </div>
        );
      },
    },
  ], [isReviewer, previewDocId, previewContentKind, navigate]);

  const hashtagManualFind = accordionFindQuery.hashtag_manual ?? '';
  const hashtagContentFind = accordionFindQuery.hashtag_content ?? '';
  const ocrContentFind = accordionFindQuery.ocr_content ?? '';

  const hashtagManualColumns = useMemo(
    () => withTableFindHighlight(hashtagColumns, hashtagManualFind),
    [hashtagColumns, hashtagManualFind]
  );
  const hashtagContentColumns = useMemo(
    () => withTableFindHighlight(hashtagColumns, hashtagContentFind),
    [hashtagColumns, hashtagContentFind]
  );
  const ocrContentColumns = useMemo(
    () => withTableFindHighlight(hashtagColumns, ocrContentFind),
    [hashtagColumns, ocrContentFind]
  );

  const getHashtagRowFindText = useCallback(
    (doc: HashtagSearchResult) => buildHashtagRowFindText(doc, formatFileSize),
    []
  );

  const filteredHashtagManualResults = useMemo(
    () => filterRowsByTableFind(hashtagManualResults, hashtagManualFind, getHashtagRowFindText),
    [hashtagManualResults, hashtagManualFind, getHashtagRowFindText]
  );
  const filteredHashtagContentResults = useMemo(
    () => filterRowsByTableFind(hashtagContentResults, hashtagContentFind, getHashtagRowFindText),
    [hashtagContentResults, hashtagContentFind, getHashtagRowFindText]
  );
  const filteredOcrHashtagResults = useMemo(
    () => filterRowsByTableFind(ocrHashtagResults, ocrContentFind, getHashtagRowFindText),
    [ocrHashtagResults, ocrContentFind, getHashtagRowFindText]
  );

  const tableFindEmptyState = (
    <div style={{ padding: 'var(--spacing-6) var(--spacing-4)', textAlign: 'center' }}>
      <p style={{ margin: 0, fontWeight: 600, color: 'var(--color-gray-700)' }}>No rows match</p>
      <p style={{ margin: '8px 0 0', fontSize: 'var(--font-size-sm)', color: 'var(--color-gray-500)' }}>
        Nothing on this page contains your search text. Try another word or go to another page.
      </p>
    </div>
  );

  const caseColumns: TableColumn<CaseSearchResult>[] = [
    {
      key: 'file_number',
      header: 'File No',
      minWidth: 170,
      clampLines: 2,
      tooltip: (c) => c.fir_number || c.cms_number || '—',
      cellStyle: { fontWeight: 600, color: 'var(--color-primary)' },
      render: (c) => {
        const hasConfidential = Array.isArray((c as any).documents) &&
          (c as any).documents.some((d: any) => d.classification === 'Confidential');

        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {c.fir_number || c.cms_number || '—'}
            {hasConfidential && (
              <span
                title="Contains confidential documents"
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 3,
                  padding: '1px 7px', borderRadius: '999px',
                  background: 'var(--color-error-light)',
                  color: 'var(--color-error)',
                  fontSize: '10px', fontWeight: 600,
                }}
              >
                <Lock size={10} /> Confidential
              </span>
            )}
          </div>
        );
      },
    },
    {
      key: 'year',
      header: 'Year',
      minWidth: 90,
      tooltip: (c) => String(c.year ?? '—'),
      render: (c) => c.year ?? '—',
    },
    {
      key: 'hierarchy_path',
      header: 'Section',
      minWidth: 190,
      clampLines: 2,
      tooltip: (c) => (c as any).hierarchy_path || '—',
      render: (c) => (c as any).hierarchy_path || '—',
    },
    {
      key: 'category',
      header: 'Category',
      minWidth: 120,
      clampLines: 2,
      tooltip: (c) => (c as any).category || '—',
      render: (c) => (c as any).category || '—',
    },
    {
      key: 'subject',
      header: 'Subject',
      minWidth: 200,
      clampLines: 2,
      tooltip: (c) => (c as any).subject || '—',
      render: (c) => (c as any).subject || '—',
    },
    {
      key: 'documents',
      header: 'Documents',
      minWidth: 120,
      tooltip: (c) => String(c.document_count ?? 0),
      render: (c) => {
        const hasConfidential = Array.isArray((c as any).documents) &&
          (c as any).documents.some((d: any) => d.classification === 'Confidential');

        return (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            <FileText size={13} style={{ color: 'var(--color-gray-400)' }} />
            {c.document_count ?? 0}
            {hasConfidential && (
              <ShieldAlert size={12} style={{ color: 'var(--color-error)', marginLeft: 2 }} />
            )}
          </span>
        );
      },
    },
    {
      key: 'actions',
      header: 'Actions',
      minWidth: 160,
      tooltip: () => 'View documents',
      render: (c) => (
        <button
          type="button"
          onClick={() => navigateToCaseDetail(c.case_id)}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
            padding: '5px 12px',
            borderRadius: 'var(--radius-md)',
            border: '1px solid var(--color-primary)',
            background: 'transparent',
            color: 'var(--color-primary)',
            fontSize: 'var(--font-size-sm)',
            fontWeight: 500,
            cursor: 'pointer',
            transition: 'background 0.15s, color 0.15s',
            whiteSpace: 'nowrap',
          }}
          onMouseEnter={e => {
            e.currentTarget.style.background = 'var(--color-primary)';
            e.currentTarget.style.color = 'white';
          }}
          onMouseLeave={e => {
            e.currentTarget.style.background = 'transparent';
            e.currentTarget.style.color = 'var(--color-primary)';
          }}
        >
          View Documents
        </button>
      ),
    },
  ];

  const runHashtagSearch = async (
    includeDocumentContent: boolean,
    tagsToSearch: string[]
  ) => {
    if (tagsToSearch.length === 0) {
      toast.error('Add at least one hashtag before searching.');
      return;
    }

    const requestId = ++hashtagSearchRequestRef.current;
    setLastHashtagSearchIncludedContent(includeDocumentContent);
    searchedHashtagTagsRef.current = tagsToSearch;
    setHashtagManualPage(1);
    setHashtagContentPage(1);
    setIsHashtagSearching(true);
    setHashtagManualResults([]);
    setHashtagManualPagination(null);
    setHashtagContentResults([]);
    setHashtagContentPagination(null);
    setHashtagSearchAttempted(false);

    try {
      const manualRes = await searchService.searchByHashtag(tagsToSearch, 1, SEARCH_DOC_LIMIT);
      if (hashtagSearchRequestRef.current !== requestId) return;

      setHashtagManualResults(manualRes.data ?? []);
      setHashtagManualPagination(manualRes.pagination ?? null);

      if (includeDocumentContent) {
        const contentRes = await searchService.searchByOcrHashtag(tagsToSearch, 1, SEARCH_DOC_LIMIT);
        if (hashtagSearchRequestRef.current !== requestId) return;
        setHashtagContentResults(contentRes.data ?? []);
        setHashtagContentPagination(contentRes.pagination ?? null);
      }

      if (hashtagSearchRequestRef.current !== requestId) return;
      setHashtagSearchAttempted(true);
    } catch {
      if (hashtagSearchRequestRef.current !== requestId) return;
      setHashtagSearchAttempted(false);
      toast.error('Search failed');
    } finally {
      if (hashtagSearchRequestRef.current === requestId) {
        setIsHashtagSearching(false);
      }
    }
  };

  const prepareHashtagTagsForSearch = (): string[] => {
    const { tags, clearedInput } = tagsWithPendingInput(hashtagTags, hashtagInput);
    if (clearedInput) setHashtagInput('');
    if (tags.length !== hashtagTags.length) {
      prevHashtagTagsRef.current = tags;
      setHashtagTags(tags);
    }
    return tags;
  };

  const prepareOcrHashtagTagsForSearch = (): string[] => {
    const { tags, clearedInput } = tagsWithPendingInput(ocrHashtagTags, ocrHashtagInput);
    if (clearedInput) setOcrHashtagInput('');
    if (tags.length !== ocrHashtagTags.length) {
      prevOcrHashtagTagsRef.current = tags;
      setOcrHashtagTags(tags);
    }
    return tags;
  };

  const handleHashtagSearchClick = () => {
    const tags = prepareHashtagTagsForSearch();
    if (tags.length === 0) {
      toast.error('Add at least one hashtag before searching.');
      return;
    }
    setIncludeDocumentContentInHashtagSearch(false);
    setIsHashtagOptionsOpen(true);
  };

  const handleConfirmHashtagSearch = () => {
    const tags = prepareHashtagTagsForSearch();
    if (tags.length === 0) {
      toast.error('Add at least one hashtag before searching.');
      return;
    }
    setIsHashtagOptionsOpen(false);
    void runHashtagSearch(includeDocumentContentInHashtagSearch, tags);
  };

  const runOcrHashtagSearchWithTags = async (tagsToSearch: string[]) => {
    if (tagsToSearch.length === 0) {
      toast.error('Add at least one term before searching.');
      return;
    }

    const requestId = ++ocrHashtagSearchRequestRef.current;
    searchedOcrHashtagTagsRef.current = tagsToSearch;
    setOcrHashtagPage(1);
    setIsOcrHashtagSearching(true);
    setOcrHashtagResults([]);
    setOcrHashtagPagination(null);
    setOcrHashtagSearchAttempted(false);

    try {
      const docRes = await searchService.searchByOcrHashtag(tagsToSearch, 1, SEARCH_DOC_LIMIT);
      if (ocrHashtagSearchRequestRef.current !== requestId) return;
      setOcrHashtagResults(docRes.data ?? []);
      setOcrHashtagPagination(docRes.pagination ?? null);
      setOcrHashtagSearchAttempted(true);
    } catch {
      if (ocrHashtagSearchRequestRef.current !== requestId) return;
      setOcrHashtagSearchAttempted(false);
      toast.error('Document content search failed');
    } finally {
      if (ocrHashtagSearchRequestRef.current === requestId) {
        setIsOcrHashtagSearching(false);
      }
    }
  };

  const handleOcrHashtagSearch = () => {
    const tags = prepareOcrHashtagTagsForSearch();
    void runOcrHashtagSearchWithTags(tags);
  };

  const navigateToCaseDetail = useCallback(
    (caseId: string | number) => {
      navigate(ROUTES.CASE_DETAIL.replace(':caseId', String(caseId)), {
        state: {
          returnTo: 'search-documents',
          searchState: {
            searchMode,
            filters,
            appliedFilters,
            page,
            hashtagTags,
            ocrHashtagTags,
          } satisfies SearchRestoreState,
        },
      });
    },
    [navigate, searchMode, filters, appliedFilters, page, hashtagTags, ocrHashtagTags]
  );

  const folderTypeOpts   = folderTypes.map((f: any)     => ({ id: String(f.id ?? f.value ?? ''), name: f.name ?? f.label ?? String(f.value ?? '') }));
  const docTypeOpts      = documentTypes.map((d: any)   => ({ id: String(d.id ?? d.value ?? ''), name: d.name ?? d.label ?? String(d.value ?? '') }));
  const extOpts          = FILE_EXTENSIONS.filter(Boolean).map((e) => ({ id: e, name: e }));

  return (
    <div className="">
      <PageHeader
        title="Search Documents"
        subtitle="Find cases and documents using filters or tags"
        actions={
          searchMode === 'filter' && (
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => {
                  // When opening the modal, start from currently applied filters
                  setFilters(appliedFilters);
                  setIsFilterModalOpen(true);
                }}
                className="rounded-md bg-[var(--color-primary)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--color-primary-light)]"
              >
                Filter
              </button>
              {hasActiveFilters && (
                <button
                  type="button"
                  onClick={handleClearFilters}
                  className="rounded-md border border-[var(--color-gray-300)] px-4 py-2 text-sm font-medium text-[var(--color-gray-700)] hover:bg-[var(--color-gray-50)]"
                >
                  Clear Filters
                </button>
              )}
            </div>
          )
        }
      />

      {/* Mode toggle */}
      <div className="mb-4 flex gap-2">
        <button
          type="button"
          onClick={() => setSearchMode('filter')}
          style={{
            padding: 'var(--spacing-2) var(--spacing-4)',
            background: searchMode === 'filter' ? 'var(--color-primary)' : 'white',
            color: searchMode === 'filter' ? 'white' : 'var(--color-gray-600)',
            border: '1px solid var(--color-gray-200)',
            borderRadius: 'var(--radius-md)', cursor: 'pointer',
            fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-medium)',
            display: 'flex', alignItems: 'center', gap: '6px'
          }}
        >
          <Search size={14} /> Filter Search
        </button>
        <button
          type="button"
          onClick={() => setSearchMode('hashtag')}
          style={{
            padding: 'var(--spacing-2) var(--spacing-4)',
            background: searchMode === 'hashtag' ? 'var(--color-primary)' : 'white',
            color: searchMode === 'hashtag' ? 'white' : 'var(--color-gray-600)',
            border: '1px solid var(--color-gray-200)',
            borderRadius: 'var(--radius-md)', cursor: 'pointer',
            fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-medium)'
          }}
        >
          # Hashtag Search
        </button>
        <button
          type="button"
          onClick={() => setSearchMode('ocr_hashtag')}
          style={{
            padding: 'var(--spacing-2) var(--spacing-4)',
            background: searchMode === 'ocr_hashtag' ? 'var(--color-primary)' : 'white',
            color: searchMode === 'ocr_hashtag' ? 'white' : 'var(--color-gray-600)',
            border: '1px solid var(--color-gray-200)',
            borderRadius: 'var(--radius-md)', cursor: 'pointer',
            fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-medium)'
          }}
        >
          # Search by Document Content
        </button>
      </div>

      {searchMode === 'hashtag' && (
        <div style={{ marginBottom: 'var(--spacing-4)' }}>
          <div style={{
            display: 'flex', flexWrap: 'wrap', gap: 'var(--spacing-2)',
            padding: 'var(--spacing-3)', border: '1px solid var(--color-gray-200)',
            borderRadius: 'var(--radius-md)', background: 'white', alignItems: 'center',
            minHeight: '48px'
          }}>
            {hashtagTags.map((tag) => (
              <span key={tag} style={{
                display: 'inline-flex', alignItems: 'center', gap: 'var(--spacing-1)',
                padding: '2px 10px', background: 'var(--color-primary-50)',
                color: 'var(--color-primary)', borderRadius: '999px',
                fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-medium)'
              }}>
                #{tag}
                <button
                  type="button"
                  onClick={() => setHashtagTags(prev => prev.filter(t => t !== tag))}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, lineHeight: 1, color: 'inherit' }}
                >
                  ×
                </button>
              </span>
            ))}
            <input
              type="text"
              value={hashtagInput}
              onChange={(e) => setHashtagInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleHashtagSearchClick();
                  return;
                }
                if (e.key === ',' && hashtagInput.trim()) {
                  e.preventDefault();
                  const tag = normalizeSearchTag(hashtagInput);
                  if (tag && !hashtagTags.includes(tag)) {
                    setHashtagTags((prev) => [...prev, tag]);
                  }
                  setHashtagInput('');
                }
                if (e.key === 'Backspace' && !hashtagInput && hashtagTags.length > 0) {
                  setHashtagTags(prev => prev.slice(0, -1));
                }
              }}
              placeholder={hashtagTags.length === 0 ? 'Type a hashtag and press Enter or comma...' : 'Add more...'}
              style={{ flex: 1, minWidth: '180px', border: 'none', outline: 'none', fontSize: 'var(--font-size-sm)', fontFamily: 'inherit' }}
            />
          </div>
          <button
            type="button"
            onClick={handleHashtagSearchClick}
            disabled={isHashtagSearching}
            style={{
              marginTop: 'var(--spacing-3)',
              padding: 'var(--spacing-2) var(--spacing-6)',
              background: isHashtagSearching ? 'var(--color-gray-100)' : 'var(--color-primary)',
              color: isHashtagSearching ? 'var(--color-gray-400)' : 'white',
              border: 'none', borderRadius: 'var(--radius-md)', cursor: isHashtagSearching ? 'not-allowed' : 'pointer',
              fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-semibold)'
            }}
          >
            {isHashtagSearching ? (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                <Spinner size={16} />
                Searching...
              </span>
            ) : (
              'Search by Hashtags'
            )}
          </button>

          {(isHashtagSearching || hashtagSearchAttempted) && (
            <>
              {isHashtagSearching ? (
                <SearchLoadingPanel message="Searching documents..." />
              ) : resultCount(hashtagManualResults, hashtagManualPagination) === 0 &&
              (!lastHashtagSearchIncludedContent ||
                resultCount(hashtagContentResults, hashtagContentPagination) === 0) ? (
                <div style={{ marginTop: 'var(--spacing-4)' }}>
                  <EmptyState
                    title="No data found"
                    description={
                      lastHashtagSearchIncludedContent
                        ? 'No documents matched your hashtags or document content. Try different tags.'
                        : 'No documents match the hashtags you entered. Try different tags or include document content search.'
                    }
                  />
                </div>
              ) : (
                <>
                  <SearchResultAccordion
                    label="Hashtags"
                    count={resultCount(hashtagManualResults, hashtagManualPagination)}
                    expanded={isAccordionOpen('hashtag_manual')}
                    onExpandedChange={(open) => setAccordionOpen('hashtag_manual', open)}
                    isLoading={hashtagManualPageLoading}
                  >
                    {hashtagManualResults.length === 0 ? (
                      <EmptyState
                        title="No data found"
                        description="No documents match hashtags for these tags."
                      />
                    ) : (
                      <AccordionTableFindPanel
                        query={hashtagManualFind}
                        onQueryChange={(q) => setAccordionFind('hashtag_manual', q)}
                        filteredCount={filteredHashtagManualResults.length}
                        totalOnPage={hashtagManualResults.length}
                      >
                        <Table
                          columns={hashtagManualColumns}
                          rows={filteredHashtagManualResults}
                          rowKey={(doc) => String(doc.document_id)}
                          minWidth={1100}
                          pagination={toTablePagination(hashtagManualPagination, hashtagManualPage)}
                          onPageChange={handleHashtagManualPageChange}
                          emptyState={hashtagManualFind.trim() ? tableFindEmptyState : undefined}
                        />
                      </AccordionTableFindPanel>
                    )}
                  </SearchResultAccordion>
                  {lastHashtagSearchIncludedContent && (
                    <SearchResultAccordion
                      label="Document content"
                      count={resultCount(hashtagContentResults, hashtagContentPagination)}
                      expanded={isAccordionOpen('hashtag_content')}
                      onExpandedChange={(open) => setAccordionOpen('hashtag_content', open)}
                      isLoading={hashtagContentPageLoading}
                    >
                      {hashtagContentResults.length === 0 ? (
                        <EmptyState
                          title="No data found"
                          description="No documents match document content for these tags."
                        />
                      ) : (
                        <AccordionTableFindPanel
                          query={hashtagContentFind}
                          onQueryChange={(q) => setAccordionFind('hashtag_content', q)}
                          filteredCount={filteredHashtagContentResults.length}
                          totalOnPage={hashtagContentResults.length}
                        >
                          <Table
                            columns={hashtagContentColumns}
                            rows={filteredHashtagContentResults}
                            rowKey={(doc) => String(doc.document_id)}
                            minWidth={1100}
                            pagination={toTablePagination(hashtagContentPagination, hashtagContentPage)}
                            onPageChange={handleHashtagContentPageChange}
                            emptyState={hashtagContentFind.trim() ? tableFindEmptyState : undefined}
                          />
                        </AccordionTableFindPanel>
                      )}
                    </SearchResultAccordion>
                  )}
                </>
              )}
            </>
          )}
        </div>
      )}

      {searchMode === 'ocr_hashtag' && (
        <div style={{ marginBottom: 'var(--spacing-4)' }}>
          <div style={{
            display: 'flex', flexWrap: 'wrap', gap: 'var(--spacing-2)',
            padding: 'var(--spacing-3)', border: '1px solid var(--color-gray-200)',
            borderRadius: 'var(--radius-md)', background: 'white', alignItems: 'center',
            minHeight: '48px'
          }}>
            {ocrHashtagTags.map((tag) => (
              <span key={tag} style={{
                display: 'inline-flex', alignItems: 'center', gap: 'var(--spacing-1)',
                padding: '2px 10px', background: 'var(--color-info-light)',
                color: 'var(--color-info)', borderRadius: '999px',
                fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-medium)'
              }}>
                #{tag}
                <button
                  type="button"
                  onClick={() => setOcrHashtagTags(prev => prev.filter(t => t !== tag))}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, lineHeight: 1, color: 'inherit' }}
                >
                  ×
                </button>
              </span>
            ))}
            <input
              type="text"
              value={ocrHashtagInput}
              onChange={(e) => setOcrHashtagInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  const { tags, clearedInput } = tagsWithPendingInput(
                    ocrHashtagTags,
                    ocrHashtagInput
                  );
                  if (clearedInput) setOcrHashtagInput('');
                  if (tags.length !== ocrHashtagTags.length) {
                    prevOcrHashtagTagsRef.current = tags;
                    setOcrHashtagTags(tags);
                  }
                  void runOcrHashtagSearchWithTags(tags);
                  return;
                }
                if ((e.key === 'Enter' || e.key === ',') && ocrHashtagInput.trim()) {
                  e.preventDefault();
                  const tag = normalizeSearchTag(ocrHashtagInput);
                  if (tag && !ocrHashtagTags.includes(tag)) {
                    setOcrHashtagTags((prev) => [...prev, tag]);
                  }
                  setOcrHashtagInput('');
                }
                if (e.key === 'Backspace' && !ocrHashtagInput && ocrHashtagTags.length > 0) {
                  setOcrHashtagTags(prev => prev.slice(0, -1));
                }
              }}
              placeholder={ocrHashtagTags.length === 0 ? 'Type a term and press Enter or comma...' : 'Add more...'}
              style={{ flex: 1, minWidth: '180px', border: 'none', outline: 'none', fontSize: 'var(--font-size-sm)', fontFamily: 'inherit' }}
            />
          </div>
          <button
            type="button"
            onClick={handleOcrHashtagSearch}
            disabled={isOcrHashtagSearching}
            style={{
              marginTop: 'var(--spacing-3)',
              padding: 'var(--spacing-2) var(--spacing-6)',
              background: isOcrHashtagSearching ? 'var(--color-gray-100)' : 'var(--color-primary)',
              color: isOcrHashtagSearching ? 'var(--color-gray-400)' : 'white',
              border: 'none', borderRadius: 'var(--radius-md)', cursor: isOcrHashtagSearching ? 'not-allowed' : 'pointer',
              fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-semibold)'
            }}
          >
            {isOcrHashtagSearching ? (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                <Spinner size={16} />
                Searching...
              </span>
            ) : (
              'Search by Document Content'
            )}
          </button>

          {(isOcrHashtagSearching || ocrHashtagSearchAttempted) && (
            <>
              {isOcrHashtagSearching ? (
                <SearchLoadingPanel message="Searching document content..." />
              ) : resultCount(ocrHashtagResults, ocrHashtagPagination) === 0 ? (
                <div style={{ marginTop: 'var(--spacing-4)' }}>
                  <EmptyState
                    title="No data found"
                    description="No documents match document content for these tags. Try different tags."
                  />
                </div>
              ) : (
                <div style={{ marginTop: 'var(--spacing-4)', position: 'relative' }}>
                  {ocrHashtagPageLoading && (
                    <div
                      style={{
                        position: 'absolute',
                        inset: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: 'rgba(255,255,255,0.7)',
                        zIndex: 1,
                        borderRadius: 'var(--radius-md)',
                      }}
                    >
                      <Spinner size={24} />
                    </div>
                  )}
                  <p
                    style={{
                      margin: '0 0 var(--spacing-3)',
                      fontSize: 'var(--font-size-sm)',
                      fontWeight: 'var(--font-weight-semibold)',
                      color: 'var(--color-gray-700)',
                    }}
                  >
                    {resultCount(ocrHashtagResults, ocrHashtagPagination)} document
                    {resultCount(ocrHashtagResults, ocrHashtagPagination) !== 1 ? 's' : ''} found in
                    document content
                  </p>
                  <div style={{ opacity: ocrHashtagPageLoading ? 0.6 : 1 }}>
                    <AccordionTableFindPanel
                      query={ocrContentFind}
                      onQueryChange={(q) => setAccordionFind('ocr_content', q)}
                      filteredCount={filteredOcrHashtagResults.length}
                      totalOnPage={ocrHashtagResults.length}
                    >
                      <Table
                        columns={ocrContentColumns}
                        rows={filteredOcrHashtagResults}
                        rowKey={(doc) => String(doc.document_id)}
                        minWidth={1100}
                        pagination={toTablePagination(ocrHashtagPagination, ocrHashtagPage)}
                        onPageChange={handleOcrHashtagPageChange}
                        emptyState={ocrContentFind.trim() ? tableFindEmptyState : undefined}
                      />
                    </AccordionTableFindPanel>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {isHashtagOptionsOpen && (
        <div
          className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/40 p-4"
          role="presentation"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setIsHashtagOptionsOpen(false);
          }}
        >
          <div
            className="flex w-full max-w-md flex-col overflow-hidden rounded-lg bg-white shadow-xl"
            role="dialog"
            aria-modal="true"
            aria-labelledby="hashtag-search-options-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-[var(--color-gray-200)] px-5 py-4">
              <h2
                id="hashtag-search-options-title"
                className="m-0 text-lg font-semibold text-[var(--color-gray-900)]"
              >
                Search options
              </h2>
              <button
                type="button"
                onClick={() => setIsHashtagOptionsOpen(false)}
                className="flex h-8 w-8 items-center justify-center rounded-md border-0 bg-transparent text-xl leading-none text-[var(--color-gray-500)] hover:bg-[var(--color-gray-100)] hover:text-[var(--color-gray-700)]"
                aria-label="Close"
              >
                ×
              </button>
            </div>

            <div className="px-5 py-4">
              <p className="m-0 text-sm leading-relaxed text-[var(--color-gray-600)]">
                Search hashtags on uploaded documents. You can also search the same terms in
                document content.
              </p>

              <label className="mt-4 flex cursor-pointer items-center gap-3 rounded-md border border-[var(--color-gray-200)] bg-[var(--color-gray-50)] px-4 py-3 transition-colors hover:border-[var(--color-primary-light)] has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-[var(--color-primary-light)]">
                <input
                  type="checkbox"
                  checked={includeDocumentContentInHashtagSearch}
                  onChange={(e) => setIncludeDocumentContentInHashtagSearch(e.target.checked)}
                  className="h-4 w-4 shrink-0 cursor-pointer accent-[var(--color-primary)]"
                />
                <span className="text-sm font-medium leading-tight text-[var(--color-gray-800)]">
                  Also search in document content
                </span>
              </label>
            </div>

            <div className="flex justify-end gap-2 border-t border-[var(--color-gray-200)] px-5 py-4">
              <Button variant="ghost" onClick={() => setIsHashtagOptionsOpen(false)}>
                Cancel
              </Button>
              <Button type="button" onClick={handleConfirmHashtagSearch}>
                Search
              </Button>
            </div>
          </div>
        </div>
      )}

      {searchMode === 'filter' && isFilterModalOpen && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.4)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
        >
          <div
            style={{
              background: 'white',
              borderRadius: 'var(--radius-lg)',
              width: '100%',
              maxWidth: 900,
              maxHeight: '80vh',
              overflowY: 'auto',
              padding: 'var(--spacing-6)',
              boxShadow: 'var(--shadow-xl)',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: 'var(--spacing-4)',
              }}
            >
              <h3
                style={{
                  margin: 0,
                  fontSize: 'var(--font-size-base)',
                  fontWeight: 'var(--font-weight-semibold)',
                }}
              >
                Filters
              </h3>
              <button
                type="button"
                onClick={() => setIsFilterModalOpen(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: 'var(--color-gray-500)',
                  padding: 4,
                  fontSize: 'var(--font-size-xl)',
                  lineHeight: 1,
                }}
                aria-label="Close filters"
              >
                ×
              </button>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <label className="flex flex-col gap-1">
                <span className="text-md font-semibold text-[var(--color-gray-700)]">File No</span>
                <input
                  type="text"
                  value={filters.fir_number ?? ''}
                  onChange={(e) => setFilter('fir_number', e.target.value)}
                  className="rounded-md border border-[var(--color-gray-300)] px-3 py-2 text-base bg-white"
                  placeholder="e.g. 0001 or 42/2026"
                />
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-md font-semibold text-[var(--color-gray-700)]">Subject</span>
                <input
                  type="text"
                  value={filters.subject ?? ''}
                  onChange={(e) => setFilter('subject', e.target.value)}
                  className="rounded-md border border-[var(--color-gray-300)] px-3 py-2 text-base bg-white"
                  placeholder="Search by subject / description"
                />
              </label>
              <div className="flex flex-col gap-1 md:col-span-2">
                <span className="text-md font-semibold text-[var(--color-gray-700)]">Section / Sub-Section</span>
                <HierarchyNodePicker
                  key={filterResetKey}
                  permission="view"
                  onChange={(nodeId) => setFilter('node_id', nodeId ?? undefined)}
                />
              </div>
              {/* Hidden when no category master is defined in the Admin module. */}
              {categoryTemplate && (
                <label className="flex flex-col gap-1">
                  <span className="text-md font-semibold text-[var(--color-gray-700)]">{categoryLabel}</span>
                  <SearchableSelect
                    options={categoryOptions}
                    value={filters.category_node_id ? String(filters.category_node_id) : ''}
                    onChange={(val) => setFilter('category_node_id', val ? parseInt(val, 10) : undefined)}
                    placeholder={`All ${categoryLabel.toLowerCase()}`}
                    loading={lookupTemplatesQuery.isLoading || categoryNodesQuery.isLoading}
                    valueKey="id"
                  />
                </label>
              )}
              <label className="flex flex-col gap-1">
                <span className="text-md font-semibold text-[var(--color-gray-700)]">Classification</span>
                <SearchableSelect
                  options={CLASSIFICATION_OPTS}
                  value={filters.classification ?? ''}
                  onChange={(val) => setFilter('classification', val || undefined)}
                  placeholder="All classifications"
                />
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-md font-semibold text-[var(--color-gray-700)]">Year</span>
                <SearchableSelect
                  options={yearOpts}
                  value={filters.year ? String(filters.year) : ''}
                  onChange={(val) => setFilter('year', val ? parseInt(val, 10) : undefined)}
                  placeholder="All years"
                />
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-md font-semibold text-[var(--color-gray-700)]">Upload status</span>
                <SearchableSelect
                  options={UPLOAD_STATUS_OPTS}
                  value={
                    UPLOAD_STATUS_OPTS.find((o) => o.id === filters.approval_status)?.name ?? ''
                  }
                  onChange={(name) => setApprovalStatusFilter(name)}
                  placeholder="All upload statuses"
                />
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-md font-semibold text-[var(--color-gray-700)]">Folder Type</span>
                <SearchableSelect
                  options={folderTypeOpts}
                  value={filters.folder_type ?? ''}
                  onChange={(val) => {
                    setFilter('folder_type', val || undefined);
                    setFilter('document_type', undefined);
                  }}
                  placeholder="All folder types"
                  loading={folderTypesQuery.isLoading}
                />
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-md font-semibold text-[var(--color-gray-700)]">Document Type</span>
                <SearchableSelect
                  options={docTypeOpts}
                  value={filters.document_type ?? ''}
                  onChange={(val) => setFilter('document_type', val || undefined)}
                  placeholder={filters.folder_type ? 'All document types' : 'Select folder type first'}
                  disabled={!filters.folder_type}
                  loading={documentTypesQuery.isLoading}
                />
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-md font-semibold text-[var(--color-gray-700)]">File Extension</span>
                <SearchableSelect
                  options={extOpts}
                  value={filters.file_extension ?? ''}
                  onChange={(val) => setFilter('file_extension', val || undefined)}
                  placeholder="All extensions"
                />
              </label>
              <label className="flex flex-col gap-1 md:col-span-2">
                <span className="text-md font-semibold text-[var(--color-gray-700)]">Hashtags (comma-separated)</span>
                <div className="relative">
                  <input
                    type="text"
                    value={filters.hashtags ?? ''}
                    onChange={async (e) => {
                      const value = e.target.value;
                      setFilter('hashtags', value);

                      const parts = value.split(',');
                      const last = parts[parts.length - 1]
                        .trim()
                        .replace(/^#/, '');
                      if (!last || last.length < 2) {
                        setHashtagSuggestions([]);
                        return;
                      }

                      try {
                        const res = await searchService.searchByHashtag(
                          [last],
                          1,
                          10
                        );
                        const allTags = new Set<string>();
                        (res.data || []).forEach((row) => {
                          (row.hashtags || []).forEach((h) => {
                            if (
                              typeof h === 'string' &&
                              h.toLowerCase().startsWith(last.toLowerCase())
                            ) {
                              allTags.add(h);
                            }
                          });
                        });
                        setHashtagSuggestions(Array.from(allTags));
                      } catch {
                        setHashtagSuggestions([]);
                      }
                    }}
                    className="w-full rounded-md border border-[var(--color-gray-300)] px-3 py-2 text-base bg-white"
                    placeholder="#murder, #2024"
                  />
                  {hashtagSuggestions.length > 0 && (
                    <div className="absolute z-10 mt-1 max-h-52 w-full overflow-y-auto rounded-md border border-[var(--color-gray-300)] bg-white shadow-md">
                      {hashtagSuggestions.map((h) => (
                        <button
                          key={h}
                          type="button"
                          onClick={() => {
                            const current = filters.hashtags ?? '';
                            const parts = current.split(',');
                            parts[parts.length - 1] = ` ${h}`.replace(
                              /^ /,
                              '#'
                            );
                            const next = parts
                              .map((p) => p.trim())
                              .filter(Boolean)
                              .join(', ');
                            setFilter('hashtags', next);
                            setHashtagSuggestions([]);
                          }}
                          className="flex w-full items-center justify-between px-3 py-1.5 text-md hover:bg-[var(--color-primary-50)]"
                        >
                          <span>#{h}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </label>
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <Button variant="ghost" onClick={handleClearFilters}>
                Clear Filters
              </Button>
              <Button onClick={handleSearch}>Apply Filters</Button>
            </div>
          </div>
        </div>
      )}

      {searchMode === 'filter' && (
        <>
          {casesQuery.isLoading ? (
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              {[1, 2, 3].map((i) => (
                <div key={i} className="card animate-pulse h-40 rounded-lg bg-[var(--color-gray-100)]" />
              ))}
            </div>
          ) : casesQuery.isError ? (
            <EmptyState
              title="Error loading results"
              description="Try again or adjust your filters."
            />
          ) : caseItems.length === 0 ? (
            <EmptyState
              title="No results found"
              description="Try different filters."
            />
          ) : (
            <>
              <Table
                columns={caseColumns}
                rows={caseItems}
                rowKey={(c) => String(c.case_id)}
                pagination={casePagination}
                onPageChange={setPage}
                minWidth={1000}
              />
            </>
          )}
        </>
      )}

      <DocumentPreviewModal
        documentId={previewDocId}
        onClose={() => {
          setPreviewDocId(null);
          setPreviewContentKind(null);
        }}
        onPreviewContentKind={setPreviewContentKind}
      />
    </div>
  );
}
